import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import {
  ACCENT,
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";

interface NieToDniFlowProps {
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "form" | "confirm";

const MAX_FILE_BYTES = 8 * 1024 * 1024;
const DNI_CONTROL_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";

const dniIsValid = (raw: string): boolean => {
  const dni = raw.trim().toUpperCase();
  const match = dni.match(/^(\d{8})([A-Z])$/);
  if (!match) return false;
  return DNI_CONTROL_LETTERS[parseInt(match[1], 10) % 23] === match[2];
};

const FilePicker = ({
  label,
  file,
  onFile,
  error,
  translate,
}: {
  label: string;
  file: { name: string } | null;
  onFile: (name: string, base64: string) => void;
  error?: string;
  translate: (key: string) => string;
}) => {
  const inputId = `dni-file-${label.replace(/\s/g, "-")}`;
  return (
    <Field label={label} error={error}>
      <label
        htmlFor={inputId}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          borderRadius: 8,
          border: `1px dashed ${file ? ACCENT : "#d1d5db"}`,
          cursor: "pointer",
          fontSize: 14,
          color: file ? "#111827" : "#6b7280",
        }}
      >
        <GlobalIcon
          iconName={file ? "CircleCheckFilledIcon" : "DownloadIcon"}
          size="XS"
          color={file ? ACCENT : "#9ca3af"}
        />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {file
            ? file.name
            : translate("newDashboard.modifications.nieToDni.filePlaceholder")}
        </span>
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const selected = e.target.files?.[0];
          if (!selected) return;
          const reader = new FileReader();
          reader.onload = () => onFile(selected.name, String(reader.result));
          reader.readAsDataURL(selected);
        }}
      />
    </Field>
  );
};

const NieToDniFlow = ({ onClose, onSubmitted }: NieToDniFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [dni, setDni] = useState("");
  const [front, setFront] = useState<{ name: string; base64: string } | null>(null);
  const [back, setBack] = useState<{ name: string; base64: string } | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const t = (key: string) => translate(`newDashboard.modifications.nieToDni.${key}`);

  const fileTooBig = (base64: string) =>
    // ~3/4 ratio between base64 length and decoded bytes.
    (base64.length - base64.indexOf(",") - 1) * 0.75 > MAX_FILE_BYTES;

  const setFile = (
    setter: (f: { name: string; base64: string } | null) => void
  ) => (name: string, base64: string) => {
    if (fileTooBig(base64)) {
      setNotification({ message: t("fileTooBig"), type: "error", time: 5 });
      return;
    }
    setter({ name, base64 });
  };

  const formValid = dniIsValid(dni) && front !== null && back !== null;

  const submit = async () => {
    if (submitting || !front || !back) return;
    setSubmitting(true);
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.postModificationRequest("NIE_TO_DNI", {
        dni: dni.trim().toUpperCase(),
        idCardFront: front.base64,
        idCardBack: back.base64,
      });
      onSubmitted();
      setNotification({
        message: translate("newDashboard.modifications.common.successToast"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      const key =
        error?.status === 409
          ? "newDashboard.modifications.errors.alreadyInProgress"
          : "newDashboard.modifications.errors.generic";
      setNotification({ message: translate(key), type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <Field
            label={t("dni")}
            error={showErrors && !dniIsValid(dni) ? t("dniInvalid") : undefined}
          >
            <input
              style={{ ...inputStyle, textTransform: "uppercase" }}
              value={dni}
              maxLength={9}
              placeholder="12345678A"
              onChange={(e) => setDni(e.target.value)}
            />
          </Field>
          <FilePicker
            label={t("front")}
            file={front}
            onFile={setFile(setFront)}
            error={showErrors && !front ? t("required") : undefined}
            translate={translate}
          />
          <FilePicker
            label={t("back")}
            file={back}
            onFile={setFile(setBack)}
            error={showErrors && !back ? t("required") : undefined}
            translate={translate}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid) return setShowErrors(true);
              setStep("confirm");
            }}
          />
        </div>
      )}

      {step === "confirm" && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>{t("confirmDesc")}</p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("summaryDni")} value={dni.trim().toUpperCase()} />
            <SummaryRow label={t("front")} value={front?.name ?? ""} />
            <SummaryRow label={t("back")} value={back?.name ?? ""} />
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirmAndSign")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("form")}
          />
        </div>
      )}

    </ModificationModal>
  );
};

export default NieToDniFlow;
